import type { IDesignEntity, IModuleDefinition } from '@platform/core/types';
import { mainRecordTypeOf } from '@platform/engine/menu/moduleSections';
import type { IDiagramLayout } from './IDiagramLayout';
import type { IModelSources } from './IModelSources';

/** Розміри діаграми прототипу v4 (pgMdesign): полотно 1100, рамка 200, шапка 26, рядок атрибута 17, до 8 атрибутів у рамці. */
export const DIAGRAM = { W: 1100, BW: 200, HH: 26, RH: 17, ROWS: 8, PILL: 150 } as const;

/**
 * Сутності «Моделі даних» за прототипом v4 (E = [основна, ...MOD_ENTITIES[модуль]]): основний тип, далі сутності прототипу головного типу
 * (`entities` — `designEntities` визначення сервера чи пакета) без `extra` у їхньому порядку; визначення без них — інші неприховані типи
 * записів у порядку визначення. Чиста функція.
 */
export function modelSourcesOf(definition: IModuleDefinition, entities: readonly IDesignEntity[] | undefined): IModelSources {
  const main = mainRecordTypeOf(definition) ?? definition.recordTypes.find((r) => !r.hidden);
  const designed = entities ? entities.map((entity, index) => ({ entity, index })).filter(({ entity }) => !entity.extra) : null;
  return { main, entities: designed, types: designed ? [] : definition.recordTypes.filter((r) => !r.hidden && r !== main) };
}

/** Висота рамки сутності: шапка, до восьми рядків атрибутів, рядок «+N ще» за потреби й нижній відступ. */
export const boxHeight = (attrs: number): number => DIAGRAM.HH + Math.min(DIAGRAM.ROWS, attrs) * DIAGRAM.RH + (attrs > DIAGRAM.ROWS ? DIAGRAM.RH : 0) + 8;

/**
 * Розкладка діаграми сутностей (прототип v4): основна сутність угорі по центру, решта — рядами до п'яти з рівними проміжками на 70px нижче
 * й кроком рядів 230px; пігулки пов'язаних модулів (до шести) — рядом унизу. Чиста функція.
 */
export function diagramLayout(attrCounts: number[], relatedCount: number): IDiagramLayout {
  const { W, BW } = DIAGRAM;
  const kids = attrCounts.length - 1;
  const cols = Math.max(1, Math.min(5, kids));
  const mainH = boxHeight(attrCounts[0] ?? 0);
  const boxes = attrCounts.map((n, i) => {
    if (i === 0) return { x: W / 2 - BW / 2, y: 20, h: mainH };
    const row = Math.floor((i - 1) / cols);
    const col = (i - 1) % cols;
    const inRow = Math.min(cols, kids - row * cols);
    const gap = (W - inRow * BW) / (inRow + 1);
    return { x: gap + col * (BW + gap), y: 20 + mainH + 70 + row * 230, h: boxHeight(n) };
  });
  const related = Math.min(6, relatedCount);
  const height = Math.max(...boxes.map((b) => b.y + b.h)) + (related ? 110 : 20);
  const pillGap = (W - related * DIAGRAM.PILL) / (related + 1);
  return { width: W, height, boxes, related: { y: height - 90, xs: Array.from({ length: related }, (_, i) => pillGap + i * (DIAGRAM.PILL + pillGap)) } };
}
